/**
 * CartContext.tsx - Shopping Cart Context (CartLine-based)
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import toastService from '../../services/toastService';
import { SelectedOption } from '../../services/customerApi';
import { CartLine, lineSignature } from '../../utils/cartLine';

const CART_STORAGE_KEY = 'customer_cart_v3';
const LEGACY_CART_KEY = 'customer_cart';

export type { CartLine };

/** @deprecated Use CartLine */
export type CartItem = CartLine;

export interface AddLineInput {
  itemId: string;
  vendorId: string;
  vendorName: string;
  name: string;
  basePrice: number;
  unitPrice: number;
  quantity?: number;
  image?: string | null;
  selectedOptions?: SelectedOption[];
  customizations?: string[];
  note?: string;
}

interface CartContextType {
  lines: CartLine[];
  /** @deprecated use lines */
  items: CartLine[];
  vendorId: string | null;
  itemCount: number;
  totalAmount: number;
  addLine: (input: AddLineInput) => void;
  /** @deprecated use addLine */
  addItem: (item: Omit<AddLineInput, 'selectedOptions'> & { id?: string; price?: number }, quantity?: number) => void;
  updateLine: (lineId: string, patch: Partial<Pick<CartLine, 'quantity' | 'unitPrice' | 'customizations' | 'selectedOptions' | 'note'>>) => void;
  removeLine: (lineId: string) => void;
  /** @deprecated use removeLine */
  removeItem: (lineId: string) => void;
  updateQuantity: (lineId: string, quantity: number) => void;
  clearCart: () => void;
  canAddVendor: (vendorId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

function isCartLine(raw: unknown): raw is CartLine {
  if (!raw || typeof raw !== 'object') return false;
  const line = raw as CartLine;
  return typeof line.lineId === 'string' && typeof line.itemId === 'string';
}

function migrateLegacyItem(raw: Record<string, unknown>): CartLine | null {
  const itemId = String(raw.id ?? raw.itemId ?? '');
  if (!itemId) return null;

  return {
    lineId: typeof raw.lineId === 'string' ? raw.lineId : crypto.randomUUID(),
    itemId,
    vendorId: String(raw.vendorId ?? ''),
    vendorName: String(raw.vendorName ?? ''),
    name: String(raw.name ?? ''),
    basePrice: Number(raw.basePrice ?? raw.price ?? 0),
    unitPrice: Number(raw.unitPrice ?? raw.price ?? 0),
    quantity: Number(raw.quantity ?? 1),
    image: raw.image ? String(raw.image) : null,
    selectedOptions: Array.isArray(raw.selectedOptions) ? (raw.selectedOptions as SelectedOption[]) : [],
    customizations: Array.isArray(raw.customizations) ? (raw.customizations as string[]) : [],
    note: raw.note ? String(raw.note) : undefined,
  };
}

const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [vendorId, setVendorId] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(CART_STORAGE_KEY) ?? localStorage.getItem(LEGACY_CART_KEY);
    if (!stored) return;

    try {
      const cartData = JSON.parse(stored);
      const rawItems = cartData.items || cartData.lines || [];
      const loaded: CartLine[] = rawItems
        .map((raw: unknown) => (isCartLine(raw) ? raw : migrateLegacyItem(raw as Record<string, unknown>)))
        .filter(Boolean) as CartLine[];

      if (loaded.length === 0 && rawItems.length > 0) {
        localStorage.removeItem(CART_STORAGE_KEY);
        localStorage.removeItem(LEGACY_CART_KEY);
        toastService.info('Cart was cleared due to an update — please reorder.');
        return;
      }

      if (localStorage.getItem(LEGACY_CART_KEY) && !localStorage.getItem(CART_STORAGE_KEY)) {
        toastService.info('Cart was upgraded — your items are still here.');
      }

      setLines(loaded);
      setVendorId(cartData.vendorId || loaded[0]?.vendorId || null);
      localStorage.removeItem(LEGACY_CART_KEY);
    } catch (error) {
      console.error('Failed to parse stored cart:', error);
      localStorage.removeItem(CART_STORAGE_KEY);
      localStorage.removeItem(LEGACY_CART_KEY);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({ lines, vendorId }));
  }, [lines, vendorId]);

  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
  const totalAmount = lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);

  const canAddVendor = useCallback(
    (newVendorId: string): boolean => !vendorId || vendorId === newVendorId || lines.length === 0,
    [vendorId, lines.length]
  );

  const addLine = useCallback((input: AddLineInput) => {
    if (vendorId && vendorId !== input.vendorId && lines.length > 0) {
      throw new Error('Cannot add items from multiple vendors. Please clear your cart first.');
    }

    setVendorId(input.vendorId);

    const candidate: CartLine = {
      lineId: crypto.randomUUID(),
      itemId: input.itemId,
      vendorId: input.vendorId,
      vendorName: input.vendorName,
      name: input.name,
      basePrice: input.basePrice,
      unitPrice: input.unitPrice,
      quantity: input.quantity ?? 1,
      image: input.image ?? null,
      selectedOptions: input.selectedOptions ?? [],
      customizations: input.customizations ?? [],
      note: input.note,
    };

    const signature = lineSignature(candidate);

    setLines((prev) => {
      const existingIndex = prev.findIndex((line) => lineSignature(line) === signature);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + (input.quantity ?? 1),
        };
        return updated;
      }
      return [...prev, candidate];
    });
  }, [vendorId, lines.length]);

  const addItem = useCallback((
    item: Omit<AddLineInput, 'selectedOptions'> & { id?: string; price?: number },
    quantity = 1
  ) => {
    addLine({
      itemId: item.itemId ?? item.id ?? '',
      vendorId: item.vendorId,
      vendorName: item.vendorName,
      name: item.name,
      basePrice: item.basePrice ?? item.price ?? item.unitPrice ?? 0,
      unitPrice: item.unitPrice ?? item.price ?? 0,
      quantity,
      image: item.image,
      customizations: item.customizations,
      note: item.note,
      selectedOptions: [],
    });
  }, [addLine]);

  const removeLine = useCallback((lineId: string) => {
    setLines((prev) => {
      const updated = prev.filter((line) => line.lineId !== lineId);
      if (updated.length === 0) {
        setVendorId(null);
      }
      return updated;
    });
  }, []);

  const updateQuantity = useCallback((lineId: string, quantity: number) => {
    if (quantity <= 0) {
      removeLine(lineId);
      return;
    }

    setLines((prev) =>
      prev.map((line) => (line.lineId === lineId ? { ...line, quantity } : line))
    );
  }, [removeLine]);

  const updateLine = useCallback((
    lineId: string,
    patch: Partial<Pick<CartLine, 'quantity' | 'unitPrice' | 'customizations' | 'selectedOptions' | 'note'>>
  ) => {
    setLines((prev) =>
      prev.map((line) => (line.lineId === lineId ? { ...line, ...patch } : line))
    );
  }, []);

  const clearCart = useCallback(() => {
    setLines([]);
    setVendorId(null);
  }, []);

  return (
    <CartContext.Provider
      value={{
        lines,
        items: lines,
        vendorId,
        itemCount,
        totalAmount,
        addLine,
        addItem,
        updateLine,
        removeLine,
        removeItem: removeLine,
        updateQuantity,
        clearCart,
        canAddVendor,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export { CartContext, CartProvider };
